import { createApi } from "@reduxjs/toolkit/query/react";
import { Document } from "../interfaces";
import { TextLayer } from "../../annotator/interfaces/textLayer";
import { DOCUMENT_MAX_TOKENS } from "../constants";
import { setTextLayer } from "../../annotator/store/AnnotatorSlice";
import {
  parseReduxQueryError,
  customBaseQuery,
} from "../../common/reduxQuery/helpers";

export const documentApi = createApi({
  reducerPath: "documentApi",
  baseQuery: customBaseQuery(),
  endpoints: (build) => ({
    getPdfDocument: build.query<ArrayBuffer, string>({
      query: (activeDocumentId) => ({
        url: `/api/v1/documents/${activeDocumentId}/pdf`,
        method: "GET",
        responseHandler: async (response: Response) =>
          await response.arrayBuffer(),
      }),
    }),
    getDocument: build.query<Document, string>({
      query: (documentId) => ({
        url: `/api/v1/documents/${documentId}`,
        method: "GET",
      }),
    }),
    fetchTextLayer: build.query<Array<TextLayer>, Document>({
      query: (activeDocument) => ({
        url: `/api/v1/documents/${activeDocument.id}/text-layer`,
        method: "GET",
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const mapped = data.map((textLayer, index) => ({
            ...textLayer,
            shouldRender:
              index === 0 ||
              textLayer.textMapItems.length <= DOCUMENT_MAX_TOKENS,
          }));
          dispatch(setTextLayer(mapped));
        } catch (err: unknown) {
          console.error(
            `An error occurred while retrieving the metrics for the document sets: ${parseReduxQueryError(
              err,
            )}`,
          );
        }
      },
    }),
    fetchPdfText: build.query<
      ArrayBuffer,
      { documentId: string; page: number }
    >({
      query: ({ documentId, page }) => ({
        url: `/api/v1/documents/${documentId}/page/${page}/text`,
        method: "GET",
        responseHandler: async (response: Response) =>
          await response.arrayBuffer(),
      }),
    }),
    fetchPdfPageMetaData: build.query<
      ArrayBuffer,
      { documentId: string; page: number }
    >({
      query: ({ documentId, page }) => ({
        url: `/api/v1/documents/${documentId}/page/${page}/text-layer-lite`,
        method: "GET",
        responseHandler: async (response: Response) =>
          await response.arrayBuffer(),
      }),
    }),
    fetchPdfPageImage: build.query<
      ArrayBuffer,
      { documentId: string; page: number }
    >({
      query: ({ documentId, page }) => ({
        url: `/api/v1/documents/${documentId}/page/${page}/image`,
        method: "GET",
        responseHandler: async (response: Response) =>
          await response.arrayBuffer(),
      }),
    }),
  }),
});

export const {
  useLazyGetPdfDocumentQuery,
  useLazyGetDocumentQuery,
  useFetchTextLayerQuery,
  useLazyFetchPdfPageImageQuery,
  useLazyFetchPdfPageMetaDataQuery,
  useLazyFetchPdfTextQuery,
} = documentApi;
